/**
 * User-written company / charter-ops brief.
 *
 * Lives on CompanyProfile (Nanode snapshot + career). Grok Inbox Fetch and the
 * schedule brain read it as live desk context. Classic dest rank still applies
 * the house lock when the box is empty: exec first, hub backup, hubs primary
 * for missed-connection / mx.
 *
 * We do not fine-tune in the xAI console — this text + the in-repo airport
 * doctrine is the training.
 */

export const CHARTER_OPS_BRIEF_TITLE_MAX = 80;
export const CHARTER_OPS_BRIEF_BODY_MAX = 8000;

export const DEFAULT_CHARTER_OPS_BRIEF_TITLE = "Executive charter";

export const DEFAULT_CHARTER_OPS_BRIEF = [
  "On-demand private charter. Typical tails follow Fleet. Origins follow company Home Base and each airframe Base — not a leftover fiction pad.",
  "We fly private leisure and business charters — not airline replacement. Prefer smaller executive / GA / reliever fields closer to the customer. Landing fees and expenses at big international / commercial hubs are unwanted for everyday private charter.",
  "Want more airport variety and fewer big airports. If no suitable executive field is on Destinations or usable, a commercial hub is the backup — never leave an RFQ without a dest. Hubs as a primary stay for missed-connection / catch-airline stories and mx positioning to the shop.",
].join("\n\n");

export function clampCharterOpsBriefTitle(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, CHARTER_OPS_BRIEF_TITLE_MAX);
}

export function clampCharterOpsBriefBody(raw: string): string {
  return raw.slice(0, CHARTER_OPS_BRIEF_BODY_MAX);
}

/** Missing field (older snapshot) → house example. Explicit empty string stays empty. */
export function hydrateCharterOpsBriefTitle(raw?: unknown): string {
  if (typeof raw !== "string") return DEFAULT_CHARTER_OPS_BRIEF_TITLE;
  return clampCharterOpsBriefTitle(raw);
}

export function hydrateCharterOpsBrief(raw?: unknown): string {
  if (typeof raw !== "string") return DEFAULT_CHARTER_OPS_BRIEF;
  return clampCharterOpsBriefBody(raw);
}

export function charterOpsBriefForGrok(input: { title?: string | null; body?: string | null }): string {
  const title = (input.title ?? "").trim();
  const body = (input.body ?? "").trim();
  if (!title && !body) return "";
  return [title ? `Title: ${title}` : "", body].filter(Boolean).join("\n");
}

/**
 * Block for Grok Inbox Fetch + schedule brain. Empty brief keeps the house dest
 * lock. A written brief may tighten or relax dest mix / charter type.
 */
export function formatCharterOpsBriefPromptBlock(input: {
  title?: string | null;
  body?: string | null;
}): string {
  const text = charterOpsBriefForGrok(input);
  if (!text) {
    return [
      "Company charter brief: (none written).",
      "Use the house dest order: prefer executive / GA / reliever when a sensible one exists; commercial hub is the backup if no suitable exec field is usable; hubs as a primary stay for missed-connection / catch-airline stories and mx to the shop. Never leave the RFQ without a destination. Never invent ICAOs.",
    ].join(" ");
  }
  return [
    "Company charter brief (Dispatcher-written company + the flights they want).",
    "Follow this for RFQ generation, dest mix, and charter type. It may tighten or relax the house dest order (more variety, fewer big hubs, closer-to-customer fields).",
    "Still never invent ICAOs, and never leave an RFQ without a dest. If the brief is silent on a pair, house lock applies: exec / GA first; hub backup when no suitable exec exists; hubs as primary mainly for missed-connection / mx.",
    text,
  ].join("\n");
}
